/**
 * Pure editor reducer (the automations plan-editor pattern). The AI↔batch
 * mutual exclusion lives in send-defaults.ts, the slot ops in
 * slots/slot-ops.ts — one testable place each — so no step component can
 * create an invalid combination.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenEffectiveConfig, DropGenMasterRef, DropGenSection } from '@trafficpulse/shared';
import { arrayMove } from '@dnd-kit/sortable';
import { defaultListRanking } from '@trafficpulse/shared';

import type {
  ContainerDraft,
  DraftGroupRef,
  DraftSendDefaults,
  DropGenHourOptimizerConfig,
} from '../../../drop-generator.types';
import { applyMaster, applySectionSource } from './master/own-sections';
import { applyGatewayOp, type GatewayOp } from './gateway-draft';
import { mergeSendDefaults } from './satellites/send-defaults';
import { applySlotOp, type SlotAction, withoutGroup } from './slots/slot-ops';
import { withoutSlots } from './slots/remove-slots';
import { dropKey } from './slots/group-keyed';

export type { SlotPatch } from './slots/slot-ops';

export type EditorAction =
  | { type: 'reset'; draft: ContainerDraft }
  | { type: 'setField'; field: keyof ContainerDraft; value: unknown }
  | { type: 'setHourOptimizer'; patch: Partial<DropGenHourOptimizerConfig> }
  | { type: 'setSendDefaults'; patch: Partial<DraftSendDefaults> }
  | SlotAction
  /** 19/10/2026 — several drops at once (draft positions), e.g. the ones outside the window. */
  | { type: 'slotsRemove'; indexes: number[] }
  | { type: 'groupAdd'; listGroupId: string }
  | { type: 'groupRemove'; listGroupId: string }
  | { type: 'groupMove'; from: number; to: number }
  | { type: 'groupPatch'; listGroupId: string; patch: Partial<DraftGroupRef> }
  | { type: 'gateway'; op: GatewayOp }
  | { type: 'creativePatch'; patch: Partial<ContainerDraft['creative']> }
  | { type: 'verdictPatch'; patch: Partial<ContainerDraft['verdict']> }
  | { type: 'scalingPatch'; patch: Partial<ContainerDraft['scaling']> }
  | { type: 'gatewayVerdictPatch'; patch: Partial<ContainerDraft['gatewayVerdict']> }
  | { type: 'hitAgainPatch'; patch: Partial<ContainerDraft['hitAgain']> }
  | { type: 'setSectionSource'; section: DropGenSection; own: boolean; master: DropGenEffectiveConfig | null }
  | { type: 'setMaster'; master: DropGenMasterRef | null; config: DropGenEffectiveConfig | null; campaign?: number | null };

export function editorReducer(
  state: ContainerDraft,
  action: EditorAction,
): ContainerDraft {
  switch (action.type) {
    case 'reset':
      return action.draft;
    case 'setSectionSource':
      return applySectionSource(state, action);
    case 'setMaster':
      return applyMaster(state, action.master, action.config, action.campaign);
    case 'setField':
      return { ...state, [action.field]: action.value };
    case 'setHourOptimizer':
      return {
        ...state,
        hourOptimizer: { ...state.hourOptimizer, ...action.patch },
      };
    case 'setSendDefaults':
      return { ...state, sendDefaults: mergeSendDefaults(state.sendDefaults, action.patch) };
    case 'slotAdd':
    case 'slotAddMany':
    case 'slotRemove':
    case 'slotPatch':
    case 'slotMove':
    case 'slotsAlternate':
    case 'slotsAllGroups':
      return applySlotOp(state, action);
    case 'slotsRemove':
      return { ...state, slots: withoutSlots(state.slots, action.indexes) };
    case 'groupAdd':
      if (state.groups.some((g) => g.listGroupId === action.listGroupId))
        return state;
      return {
        ...state,
        groups: [
          ...state.groups,
          { listGroupId: action.listGroupId, orderMode: 'MANUAL', manualOrderJson: null, orderFormulaJson: defaultListRanking(), listsPerDay: 1, allListsMode: 'EACH' },
        ],
      };
    case 'groupRemove':
      // Phase 21 — the group leaves every drop, its own amounts too.
      return {
        ...state,
        groups: state.groups.filter((g) => g.listGroupId !== action.listGroupId),
        slots: withoutGroup(state.slots, action.listGroupId),
      };
    case 'groupMove':
      return { ...state, groups: arrayMove(state.groups, action.from, action.to) };
    case 'groupPatch':
      return patchGroup(state, action.listGroupId, action.patch);
    case 'gateway':
      return { ...state, gateway: applyGatewayOp(state.gateway, action.op) };
    case 'creativePatch':
      return { ...state, creative: { ...state.creative, ...action.patch } };
    case 'verdictPatch':
      return { ...state, verdict: { ...state.verdict, ...action.patch } };
    case 'scalingPatch':
      return { ...state, scaling: { ...state.scaling, ...action.patch } };
    case 'gatewayVerdictPatch':
      return { ...state, gatewayVerdict: { ...state.gatewayVerdict, ...action.patch } };
    case 'hitAgainPatch':
      return { ...state, hitAgain: { ...state.hitAgain, ...action.patch } };
    default:
      return state;
  }
}

function patchGroup(
  state: ContainerDraft,
  listGroupId: string,
  patch: Partial<ContainerDraft['groups'][number]>,
): ContainerDraft {
  const groups = state.groups.map((g) => (g.listGroupId === listGroupId ? { ...g, ...patch } : g));
  // 19/10/2026 — back to One list: the group's ranks leave its drops (else they come back with All lists).
  const slots = patch.listsPerDay === 1 ? state.slots.map((s) => ({ ...s, listsRankByGroup: dropKey(s.listsRankByGroup, listGroupId) })) : state.slots;
  return { ...state, groups, slots };
}
